import {
  Field,
  FieldModel,
  ProgramEngagementStatus,
  ProgramTaskContent,
  ProgramTaskRole,
  TaskContentDetailsLocked,
} from '@cibo/core'
import {
  PracticeSelectionSummaryTable,
  PracticesSelectedCell,
  ResourceConcernAddressedCell,
  fieldNameColumn,
} from '@cibo/landmanager'
import {
  useProgramEngagement,
  useProgramEngagementConfirmTask,
  useUpdateProgramEngagement,
} from '@cibo/programs'
import { DataGridPro, Markdown, Section } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import { Alert, AlertTitle, Box, Grid, Stack, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { UseQueryResult } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

type DetailsLockedPracticeConfirmationProps = {
  handleLockDetails: () => void
  taskState?: ProgramEngagementStatus
  taskContent?: ProgramTaskContent<TaskContentDetailsLocked>
  fields: UseQueryResult<{ items: FieldModel[] }>
  userRole: ProgramTaskRole
  ownerName: string
}

export const DetailsLockedPracticeConfirmation = ({
  handleLockDetails,
  taskState,
  taskContent,
  fields,
  userRole,
  ownerName,
}: DetailsLockedPracticeConfirmationProps) => {
  const { t } = useTranslation('@cibo/programs/DetailsLockedPracticeConfirmation')
  const nameColumn = fieldNameColumn({ t })
  const lockDetails = useProgramEngagementConfirmTask()
  const { engagementId, taskId } = useParams<any>()
  const engagement = useProgramEngagement({ id: engagementId })
  const updateEngagement = useUpdateProgramEngagement()
  const [highPractices, setHighPractices] = useState<string[]>([])

  const columns = useMemo(
    () =>
      [
        nameColumn,
        {
          field: 'resourceConcern',
          headerName: t('resourceConcern'),
          flex: 1,
          sortable: false,
          filterable: false,
          renderCell: ResourceConcernAddressedCell,
        },
        {
          field: 'practicesSelected',
          headerName: t('practicesSelected'),
          flex: 1,
          sortable: false,
          filterable: false,
          renderCell: (params: GridRenderCellParams) => (
            <PracticesSelectedCell params={params} setHighPractices={setHighPractices} />
          ),
        },
      ] as GridColDef[],
    []
  )

  const canConfirm = taskState?.allowedActions?.includes('canModify')

  return (
    <>
      <Section sx={{ flex: 1 }}>
        <Stack padding={3} spacing={3}>
          {taskState?.available ? (
            <>
              <Stack
                direction="row"
                spacing={3}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Stack direction="column" spacing={1}>
                  <Typography variant="h6">{t('confirmPractices')}</Typography>
                  <Markdown>{t('confirmPrompt', { context: userRole, name: ownerName })}</Markdown>
                </Stack>
                <Box alignSelf={'flex-start'}>
                  <LoadingButton
                    loading={updateEngagement?.isLoading || lockDetails.isLoading}
                    onClick={handleLockDetails}
                    variant="contained"
                    disabled={!canConfirm}
                  >
                    {(engagement.data?.getTaskStatus(taskId)?.status.progress?.completed
                      ? taskContent?.content.completedCta
                      : undefined) ??
                      taskContent?.content.cta ??
                      t('continue')}
                  </LoadingButton>
                </Box>
              </Stack>
              {highPractices.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <AlertTitle>
                      {t('practiceAlertTitle', {
                        fieldNames: highPractices,
                        count: highPractices.length,
                      })}
                    </AlertTitle>
                    {t('practiceAlertSubtitle')}
                  </Alert>
                </Grid>
              )}
              {!!fields.data?.items.length && (
                <DataGridPro
                  columns={columns}
                  rows={fields.data?.items}
                  getRowHeight={() => 'auto'}
                  getRowId={(row: Field) => row.resourceId}
                  loading={updateEngagement.isLoading || lockDetails.isLoading}
                  autoHeight
                  disableSelectionOnClick
                  hideFooter
                  hideCellFocus
                />
              )}
            </>
          ) : (
            <>WIP: Not Available</>
          )}
        </Stack>
      </Section>
      {!!fields.data?.items.length && (
        <Section sx={{ flex: 1 }}>
          <PracticeSelectionSummaryTable
            engagementId={engagementId}
            resourceIds={fields.data?.items.map(i => i.id)}
          />
        </Section>
      )}
    </>
  )
}
